<script setup lang="ts">
import { PropType, computed } from "vue";
import { User } from "@/types/user/User";
import { ReferPromotion } from "@/types/refer_promotion/ReferPromotion";
import BgWaveCard from "@/components/organisms/common/BgWaveCard.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import ReferAttention from "@/components/organisms/mypage/referPromotion/ReferAttention.vue";
import { formatNumber } from "@/utils/number";
import { deviceModule } from "@/store/device";

const props = defineProps({
  promotion: {
    type: Object as PropType<ReferPromotion>,
    required: true
  },
  userData: {
    type: Object as PropType<User>,
    required: true
  },
  pointAppliedPromotion: {
    type: Number,
    required: true
  },
  countAppliedPromotion: {
    type: Number,
    required: true
  }
});

const fullName = computed(() => {
  return props.userData?.fullName ?? "";
});

const formattedPointAppliedPromotion = computed(() => {
  return formatNumber(props.pointAppliedPromotion);
});

const isIos = computed(() => {
  return deviceModule.isIos;
});

const sideFlowClass = computed(() => {
  return `side-flow ${isIos.value ? "" : "side-flow-not-ios"}`;
});

const emits = defineEmits(["click"]);

const onClick = () => {
  emits("click");
};
</script>

<template>
  <div>
    <h2 class="text-mild section-title">招待の手順</h2>
    <div class="d-flex flex-column mt-2 gap-8">
      <div class="d-flex step-section">
        <div :class="sideFlowClass">
          あなた
        </div>
        <div class="d-flex flex-column flex-grow-1 gap-8">
          <div>
            <span class="step-number mr-1">1</span>
            <span>招待コードを招待したい方に送る</span>
          </div>
          <BgWaveCard class="w-100">
            <div class="d-flex align-items-center">
              <p
                class="text-mild text-size-28 text-weight-700 pt-1"
                data-testid="refer-promotion-code"
              >
                {{ promotion.code }}
              </p>
              <p
                class="text-mild text-size-14 ml-auto"
                @click="onClick"
                data-testid="copy-button"
              >
                <span class="text-mild mr-1">コピー</span>
                <FeatherIcon size="20" icon="copy" />
              </p>
            </div>
          </BgWaveCard>
        </div>
      </div>
      <div class="d-flex step-section">
        <div :class="sideFlowClass">
          招待された方
        </div>
        <div class="d-flex flex-column gap-8">
          <div>
            <span class="step-number mr-1">2</span>
            <span>カイテクに登録、資格認証を行う</span>
          </div>
          <div class="d-flex align-items-start">
            <span class="step-number mr-1">3</span>
            <span
              >「マイページ＞招待キャンペーン」<br />
              から招待コードを登録</span
            >
          </div>
        </div>
      </div>
      <div class="d-flex step-section">
        <div :class="sideFlowClass">
          あなた
        </div>
        <div class="d-flex flex-column">
          <div>
            <span class="step-number mr-1">4</span>
            <span>初勤務完了後に即時ポイント付与</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ReferAttention />

  <div class="mx-2 my-4">
    <p
      class="text-mild section-title mb-2"
      data-testid="title-applied-promotion"
    >
      {{ fullName }}さまの招待実績
    </p>
    <BgWaveCard>
      <div class="d-flex">
        <div
          class="text-size-14 flex-grow-1 d-flex flex-column align-items-center border-right pr-3"
        >
          <span>獲得した仲間</span>
          <div class="font-weight-bold" data-testid="worker-applied-promotion">
            <span class="text-size-32 highlight">{{
              countAppliedPromotion
            }}</span
            ><span class="text-size-20 highlight">人</span>
          </div>
        </div>
        <div
          class="text-size-14 flex-grow-1 d-flex flex-column align-items-center pl-3"
        >
          <p>獲得したポイント</p>
          <div class="font-weight-bold" data-testid="point-applied-promotion">
            <span class="text-size-32 highlight">{{
              formattedPointAppliedPromotion
            }}</span>
            <span class="text-size-20 highlight">pt</span>
          </div>
        </div>
      </div>
    </BgWaveCard>
  </div>
</template>

<style scoped lang="scss">
.highlight {
  color: #f4a722;
}

.side-flow {
  padding: 8px 8px 24px;
  clip-path: polygon(
    0 0,
    100% 0,
    100% calc(100% - 20px),
    50% 100%,
    0 calc(100% - 20px)
  );
  font-size: 14px;
  background-color: #ffefc7;
  writing-mode: vertical-rl;
  text-orientation: upright;

  // 縦書きのレイアウトでiOS以外だと文字間のスペースが大きくなるのでiOS以外の時だけ調整
  &-not-ios {
    letter-spacing: -4px;
  }
}

.step-number {
  padding: 0 8px;
  font-weight: 200;
  color: $white;
  background-color: #fbce5a;
  border-radius: 50%;
}

.step-section {
  gap: 16px;
}

.gap-8 {
  gap: 8px;
}
</style>
